import React, { useEffect, useRef, useState } from "react";
import { STAGES } from "./trackingUtils";

const ProgressSteps = ({ stage, tone }) => {
  const edge = 50 / STAGES.length; // centre of first/last step, in %
  return (
    <div className="progress" aria-label="Shipment progress">
      <div className="progress-track" style={{ left: `${edge}%`, right: `${edge}%` }}>
        <div
          className="progress-fill"
          style={{ width: `${(stage / (STAGES.length - 1)) * 100}%` }}
        />
      </div>
      {STAGES.map((label, i) => {
        const state = i < stage ? "done" : i === stage ? `current tone-${tone}` : "";
        return (
          <div key={label} className={`progress-step ${state}`}>
            <span className="progress-dot" />
            <span className="progress-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
};

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
    <path
      d="M5 15V6a2 2 0 0 1 2-2h9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/** Copy text, with a fallback for iframes / non-HTTPS where the Clipboard API is blocked. */
const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch (err) {
      ok = false;
    }
    document.body.removeChild(el);
    return ok;
  }
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const COPIED_MS = 2000;

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const hovering = useRef(false);
  const timer = useRef(null);

  // Keep "Copied" visible for at least COPIED_MS, and until the pointer leaves
  const scheduleReset = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!hovering.current) setCopied(false);
    }, COPIED_MS);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    if (await copyText(text)) {
      setCopied(true);
      scheduleReset();
    }
  };

  return (
    <button
      type="button"
      className={`copy-btn ${copied ? "copied" : ""}`}
      onClick={copy}
      onMouseEnter={() => {
        hovering.current = true;
      }}
      onMouseLeave={() => {
        hovering.current = false;
        if (copied) scheduleReset();
      }}
      aria-label={copied ? "AWB number copied" : "Copy AWB number"}
      aria-live="polite"
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};


const StatusHero = ({ trackingId, summary }) => (
  <section className="card status-hero">
    <div className="status-hero-top">
      <div>
        {summary.badge && (
          <span className={`status-badge tone-${summary.tone}`}>{summary.badge}</span>
        )}
        <p className="status-awb">
          AWB <strong>{trackingId}</strong>
          <CopyButton text={trackingId} />
        </p>
      </div>
    </div>
    <ProgressSteps stage={summary.stage} tone={summary.tone} />
  </section>
);

export default StatusHero;
